"use client";

import { createContext, useContext, type ReactNode } from "react";
import type {
  Auth0ContextInterface,
  GetTokenSilentlyOptions,
  User,
} from "@auth0/auth0-react";

const TEST_USER: User = {
  sub: "auth0|clarityos-e2e",
  email: "clarityos-e2e@acme.test",
  email_verified: true,
  name: "ClarityOS E2E User",
  updated_at: new Date().toISOString(),
};

function base64UrlEncode(obj: Record<string, unknown>) {
  const json = JSON.stringify(obj);
  const utf8Bytes = new TextEncoder().encode(json);
  const binaryString = Array.from(utf8Bytes, (byte) =>
    String.fromCharCode(byte),
  ).join("");
  const base64 = btoa(binaryString);

  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function createMockJwt() {
  const header = {
    alg: "RS256",
    typ: "JWT",
    kid: "clarityos-test-key",
  };

  const payload = {
    email: TEST_USER.email,
    iss: "https://clarityos.mock.auth0.com/",
    sub: TEST_USER.sub,
    aud: [
      "https://clarityrcm_api",
      "https://clarityos.mock.auth0.com/userinfo",
    ],
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 60 * 60,
    scope: "openid profile email offline_access",
    azp: "clarityos-test-client",
    practiceId: ["demo-practice", "north-derm"],
    roles: ["ccof:operator"],
    permissions: [
      "read:card_collection",
      "read:patient_ar",
      "read:financial_aggregate",
    ],
    // E2E tests may replace claims, e.g. an empty `permissions` array.
    ...(typeof window !== "undefined"
      ? window.__E2E_ACCESS_TOKEN_CLAIMS__
      : undefined),
  };

  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.mock-signature`;
}

let testAccessToken: string | undefined;

// Built on first use so claims installed by an E2E init script apply.
function getTestAccessToken() {
  testAccessToken ??= createMockJwt();
  return testAccessToken;
}

const mockAuth0Context = {
  isAuthenticated: true,
  isLoading: false,
  user: TEST_USER,
  getAccessTokenSilently: (async (options?: GetTokenSilentlyOptions) => {
    if (options?.detailedResponse) {
      return {
        access_token: getTestAccessToken(),
        id_token: "mock-id-token",
        expires_in: 3600,
      };
    }

    return getTestAccessToken();
  }) as Auth0ContextInterface["getAccessTokenSilently"],
  getAccessTokenWithPopup: (async () =>
    getTestAccessToken()) as Auth0ContextInterface["getAccessTokenWithPopup"],
  getIdTokenClaims: async () => ({
    __raw: "mock-id-token",
    name: TEST_USER.name,
    email: TEST_USER.email,
    sub: TEST_USER.sub,
    aud: "https://clarityrcm_api",
    exp: Math.floor(Date.now() / 1000) + 3600,
    iat: Math.floor(Date.now() / 1000),
    iss: "https://clarityos.mock.auth0.com/",
  }),
  loginWithRedirect: async () => Promise.resolve(),
  loginWithPopup: async () => Promise.resolve(),
  logout: () => Promise.resolve(),
  handleRedirectCallback: async () => ({ appState: undefined }),
  error: undefined,
} as unknown as Auth0ContextInterface;

const ClarityTestAuth0Context =
  createContext<Auth0ContextInterface>(mockAuth0Context);

export function ClarityTestAuth0Provider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <ClarityTestAuth0Context.Provider value={mockAuth0Context}>
      {children}
    </ClarityTestAuth0Context.Provider>
  );
}

export function useClarityTestAuth0() {
  return useContext(ClarityTestAuth0Context);
}
